import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Anchor, Button, Container, Group, Skeleton, Stack, Text } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconWheat } from '@tabler/icons-react';
import EmptyState from '@/components/common/EmptyState';
import FactsStrip, { type Fact } from '@/components/common/FactsStrip';
import PageTitle from '@/components/common/PageTitle';
import BulletinSection from '@/components/common/BulletinSection';
import FormMessage from '@/components/auth/FormMessage';
import PriceMatrix, { buyAdvantage, type MarketState } from '@/components/app/buy/PriceMatrix';
import OfferTerms from '@/components/app/buy/OfferTerms';
import BulkPriceDialog from '@/components/app/buy/BulkPriceDialog';
import { orderService } from '@/api/services/OrderService';
import { BUY_TREE_QUERY_KEY, useBuyBestPrices, usePurchasePriceList, type PriceListQuery } from '@/hooks/useBuy';
import { buildBestPriceMap } from '@/lib/bestPrice';
import { bestPriceTone } from '@/lib/bestOffer';
import { priceRangeOf, withoutPriceList } from '@/lib/buyTree';
import { getApiMessage, getStatus, isServiceUnavailable } from '@/api/errors';
import { makeEditUrl } from '@/lib/buyLinks';
import { ROUTES } from '@/routes/paths';
import { formatMoney, formatOrgName } from '@/lib/format';
import type { BuyTreeResponse, PrepaymentKey } from '@/types/buy';
import classes from './BuyGroupPage.module.css';

// Минимальная цена строки на платформе (₽/т) — ограничивает массовое уменьшение.
const PRICE_FLOOR = 1000;
const PREPAYMENTS: readonly PrepaymentKey[] = ['0', '100'];
const YEAR = /^\d{4}$/;

/**
 * Параметры прайс-листа из адреса; неверная ссылка — null («не найдено»). Прайс-лист — базис и культура: год
 * необязателен (у закупки его нет, решение 2026-11-04) и приходит только от сервера выпуска А; переданный год обязан
 * быть четырёхзначным.
 */
export function parseGroupParams(params: URLSearchParams): PriceListQuery | null {
  // Значения не обрезаются: по ним сервер ищет прайс-лист, а пустые после обрезки пробелов — неверная ссылка.
  const address = params.get('address') ?? '';
  const addressTitle = params.get('addressTitle') ?? '';
  const goods = params.get('goods') ?? '';
  const year = params.get('year');
  if (!address.trim() || !addressTitle.trim() || !goods.trim() || (year !== null && !YEAR.test(year))) {
    return null;
  }
  return year === null ? { address, addressTitle, goods } : { address, addressTitle, goods, year: Number(year) };
}

function BackLink({ to }: { readonly to: string }) {
  const { t } = useTranslation();
  return (
    <Anchor component={Link} to={to} c="gray.7" fz="sm" underline="never" style={{ alignSelf: 'flex-start' }}>
      <Group gap={4} wrap="nowrap">
        <IconArrowLeft size={16} aria-hidden />
        {t('app.buy.back')}
      </Group>
    </Anchor>
  );
}

function MessageScreen({ backTo, title, children }: { readonly backTo: string; readonly title: string; readonly children: ReactNode }) {
  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <BackLink to={backTo} />
        <PageTitle c="var(--mantine-color-text)" fz={{ base: 26, sm: 30 }}>
          {title}
        </PageTitle>
        {children}
      </Stack>
    </Container>
  );
}

// Страница пересоздаётся при смене адреса: открытое окно изменения цен и незавершённые действия относятся к одному
// прайс-листу и не должны перейти на другой.
export default function BuyGroupPage() {
  const [params] = useSearchParams();
  const { t } = useTranslation();
  // Вид списка «Купить», из которого открыли прайс-лист: строка запроса с фильтром или пустая.
  const listSearch = (useLocation().state as { listSearch?: unknown } | null)?.listSearch;
  const key = params.toString();
  const query = useMemo(() => parseGroupParams(new URLSearchParams(key)), [key]);
  // Назад — к тому виду списка, из которого открыли; при прямом заходе — к списку с базисом и культурой прайс-листа.
  const backTo =
    typeof listSearch === 'string' && (listSearch === '' || listSearch.startsWith('?'))
      ? `${ROUTES.APP_BUY}${listSearch}`
      : query
        ? `${ROUTES.APP_BUY}?${new URLSearchParams({ address: query.address, addressTitle: query.addressTitle, goods: query.goods }).toString()}`
        : ROUTES.APP_BUY;
  if (!query) {
    return (
      <MessageScreen backTo={backTo} title={t('app.buy.title')}>
        <FormMessage>{t('app.buy.groupNotFound')}</FormMessage>
      </MessageScreen>
    );
  }
  return <GroupPage key={key} query={query} backTo={backTo} />;
}

// Деталь прайс-листа («Бюллетень», как страница предложения): шапка с диапазоном моих цен и полосой фактов, ряд
// действий, цены матрицей с лучшими ценами продавцов, условия строками.
function GroupPage({ query, backTo }: { readonly query: PriceListQuery; readonly backTo: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isError, error, isFetching, refetch } = usePurchasePriceList(query);

  // Лучшие цены продажи под мои строки: сбой — одно сообщение над таблицей, цены прайс-листа остаются.
  const orderIds = useMemo(
    () => data?.goodsList.flatMap((item) => item.qualityRows.map((row) => row.orderId).filter((id): id is number => id != null)) ?? [],
    [data],
  );
  const bestParams = useMemo(
    () => (orderIds.length > 0 ? { purchaseOrderIds: orderIds, address: query.address, goodsTypeParent: query.goods, cropYear: query.year } : null),
    [orderIds, query],
  );
  const bestQuery = useBuyBestPrices(bestParams);
  const bestPrices = useMemo(() => buildBestPriceMap(bestQuery.data), [bestQuery.data]);
  // Без строк с заявками рынок не запрашивается: сравнивать не с чем, состояние — «готов».
  const market: MarketState = bestParams == null || bestQuery.data ? 'ready' : bestQuery.isError ? 'failed' : 'loading';

  const range = useMemo(() => (data ? priceRangeOf(data) : null), [data]);
  const priceCount = useMemo(
    () =>
      data?.goodsList.flatMap((item) => item.qualityRows).flatMap((row) => PREPAYMENTS.filter((key) => row.pricesByPrepaymentMap?.[key] != null))
        .length ?? 0,
    [data],
  );
  // Максимально допустимое уменьшение: минимальная из моих цен не должна уйти ниже пола.
  const maxDiscount = range ? Math.max(0, range.min - PRICE_FLOOR) : 0;

  const [bulkOpened, setBulkOpened] = useState(false);
  const basisTitle = formatOrgName(query.addressTitle) || query.address;

  const onActionError = (err: unknown): void => {
    notifications.show({
      color: 'red',
      message: isServiceUnavailable(err) ? t('auth.errors.unavailable') : (getApiMessage(err) ?? t('auth.errors.generic')),
    });
  };

  const bulk = useMutation({
    mutationFn: (delta: number) =>
      orderService.updatePurchasePrices({
        address: query.address,
        addressTitle: query.addressTitle,
        parentGoodsType: query.goods,
        cropYear: query.year,
        delta,
      }),
    onSuccess: () => {
      setBulkOpened(false);
      // Цены, лучшие цены к ним, дата «Обновлён» в списке и «Лучшие предложения» на «Главной».
      for (const queryKey of [['purchasePriceList'], ['buyBestPrices'], BUY_TREE_QUERY_KEY, ['bestOffers']]) {
        void queryClient.invalidateQueries({ queryKey });
      }
      // Фокус на кнопку, открывшую окно, возвращает само окно при закрытии.
      notifications.show({ color: 'lime', message: t('app.buy.bulk.applied') });
    },
    onError: onActionError,
  });

  const deletedRef = useRef(false);
  useEffect(
    () => () => {
      if (deletedRef.current) {
        queryClient.removeQueries({ queryKey: ['purchasePriceList', query.address, query.addressTitle, query.goods, query.year] });
      }
    },
    [queryClient, query],
  );

  const removeGroup = useMutation({
    mutationFn: () =>
      orderService.deletePurchaseGroup({
        address: query.address,
        addressTitle: query.addressTitle,
        parentGoodsType: query.goods,
        year: query.year,
      }),
    onSuccess: () => {
      // Прайс-лист исчезает из списка сразу. Остальное помечается устаревшим без немедленной перезагрузки: список
      // и «Главная» загрузят свежее при открытии, а другие прайс-листы в кэше не запрашиваются зря.
      queryClient.setQueryData<BuyTreeResponse>(BUY_TREE_QUERY_KEY, (tree) => withoutPriceList(tree, query));
      for (const queryKey of [BUY_TREE_QUERY_KEY, ['purchasePriceList'], ['buyBestPrices'], ['bestOffers']]) {
        void queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
      }
      notifications.show({ color: 'lime', message: t('app.buy.del.groupDeleted', { goods: query.goods }) });
      // Уход со страницы с заменой записи истории: «назад» не ведёт на удалённый прайс-лист. Деталь удаляется из кэша
      // при размонтировании страницы (см. эффект ниже): пока страница смонтирована, удаление пересоздало бы запрос,
      // и удалённый прайс-лист загрузился бы снова с экраном «не найдено». От скорости перехода это не зависит.
      deletedRef.current = true;
      navigate(backTo, { replace: true });
    },
    onError: onActionError,
  });

  function confirmDeleteGroup(): void {
    // Число удаляемых позиций (заявок с ценой) — чтобы сотрудник видел масштаб перед удалением общего прайс-листа.
    const positions = data?.goodsList.flatMap((item) => item.qualityRows).filter((row) => row.orderId != null).length ?? 0;
    modals.openConfirmModal({
      title: t('app.buy.del.groupTitle'),
      children: <Text size="sm">{t('app.buy.del.groupConfirm', { goods: query.goods, basis: basisTitle, count: positions })}</Text>,
      labels: { confirm: t('app.buy.del.confirm'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => removeGroup.mutate(),
    });
  }

  const status = getStatus(error);
  if (isError && !data && (status === 404 || status === 403 || status === 410)) {
    return (
      <MessageScreen backTo={backTo} title={query.goods}>
        <FormMessage>{t('app.buy.groupNotFound')}</FormMessage>
      </MessageScreen>
    );
  }
  // Сбой повторной загрузки (например, после изменения цен) не прячет показанные цены: экран ошибки — только когда
  // показывать нечего.
  if (isError && !data) {
    return (
      <MessageScreen backTo={backTo} title={query.goods}>
        <FormMessage>
          <Group gap="sm">
            <span>{isServiceUnavailable(error) ? t('auth.errors.unavailable') : t('auth.errors.generic')}</span>
            <Button variant="default" size="compact-md" loading={isFetching} onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </Group>
        </FormMessage>
      </MessageScreen>
    );
  }
  if (!data) {
    // Скелет повторяет будущую разметку: шапку, полосу фактов, действия и таблицу.
    return (
      <Container size="lg" py="xl">
        <Stack gap="lg" aria-busy="true" aria-label={t('app.buy.groupLoading')}>
          <BackLink to={backTo} />
          <Stack gap="sm">
            <Skeleton h={36} w={260} />
            <Skeleton h={20} w={300} />
          </Stack>
          <Skeleton h={64} />
          <Skeleton h={42} w={360} />
          <Skeleton h={220} />
        </Stack>
      </Container>
    );
  }

  const rows = data.goodsList.flatMap((item) => item.qualityRows);
  const favorable =
    market === 'ready'
      ? rows.reduce((sum, row) => sum + PREPAYMENTS.filter((key) => bestPriceTone(buyAdvantage(row, key, bestPrices)) === 'gain').length, 0)
      : null;
  // Года у закупки нет (решение 2026-11-04): в шапке только дата изменения прайс-листа.
  const lead = data.updatedDate ? t('app.buy.updatedLower', { date: data.updatedDate }) : '';
  const facts: Fact[] = [
    { key: 'basis', label: t('app.buy.facts.basis'), value: basisTitle, note: basisTitle !== query.address ? query.address : undefined, wide: true },
    ...(rows.length > 0
      ? [
          {
            key: 'positions',
            label: t('app.buy.facts.positions'),
            value:
              data.goodsList.length > 1
                ? `${t('app.buy.facts.positionsCount', { count: rows.length })} · ${t('app.buy.facts.classesCount', { count: data.goodsList.length })}`
                : t('app.buy.facts.positionsCount', { count: rows.length }),
          },
        ]
      : []),
    ...(favorable != null && rows.length > 0 ? [{ key: 'favorable', label: t('app.buy.facts.favorable'), value: String(favorable) }] : []),
  ];
  const busy = bulk.isPending || removeGroup.isPending;

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Stack gap="sm">
          <BackLink to={backTo} />
          <div>
            <div className={classes.head}>
              <div style={{ minWidth: 0 }}>
                <PageTitle c="var(--mantine-color-text)" fz={{ base: 26, sm: 30 }}>
                  {query.goods}
                </PageTitle>
                <p className={classes.lead}>{lead}</p>
              </div>
              {range ? (
                <div className={classes.price}>
                  <p className={classes.priceLabel}>{t('app.buy.myPrices')}</p>
                  <p className={classes.priceValue}>
                    {range.min === range.max ? formatMoney(range.min) : `${formatMoney(range.min)}–${formatMoney(range.max)}`}{' '}
                    <span className={classes.priceUnit}>{t('app.deals.rubPerTon')}</span>
                  </p>
                  <p className={classes.priceLabel}>{t('app.buy.cptNoNds')}</p>
                </div>
              ) : null}
            </div>
            <FactsStrip facts={facts} />
          </div>
        </Stack>

        {/* Действия видны и у пустого прайс-листа: его можно заполнить или удалить. */}
        <div className={classes.actions}>
          {priceCount > 0 ? (
            <Button variant="default" disabled={busy} onClick={() => setBulkOpened(true)}>
              {t('app.buy.bulk.button')}
            </Button>
          ) : null}
          {busy ? (
            <Button variant="default" disabled>
              {t('app.buy.form.editButton')}
            </Button>
          ) : (
            <Button component={Link} to={makeEditUrl(query)} variant="default">
              {t('app.buy.form.editButton')}
            </Button>
          )}
          <Button variant="subtle" color="red" className={classes.remove} loading={removeGroup.isPending} disabled={busy} onClick={confirmDeleteGroup}>
            {t('app.buy.del.button')}
          </Button>
        </div>

        {isError ? (
          <FormMessage>
            <Group gap="sm">
              <span>{isServiceUnavailable(error) ? t('auth.errors.unavailable') : t('auth.errors.generic')}</span>
              <Button variant="default" size="compact-md" loading={isFetching} onClick={() => refetch()}>
                {t('common.retry')}
              </Button>
            </Group>
          </FormMessage>
        ) : null}

        {data.goodsList.length === 0 ? (
          <EmptyState decorated={false} icon={<IconWheat size={30} />} title={t('app.buy.groupEmpty')} description={t('app.buy.groupEmptyHint')} />
        ) : (
          <BulletinSection title={t('app.buy.pricesLabel')}>
            {market === 'failed' ? (
              <div className={classes.marketError}>
                <FormMessage quiet>
                  <Group gap="sm">
                    <span>{t('app.buy.marketError')}</span>
                    <Button variant="default" size="compact-md" loading={bestQuery.isFetching} onClick={() => bestQuery.refetch()}>
                      {t('common.retry')}
                    </Button>
                  </Group>
                </FormMessage>
              </div>
            ) : null}
            <PriceMatrix list={data} bestPrices={bestPrices} market={market} />
          </BulletinSection>
        )}

        <OfferTerms list={data} />

        <BulkPriceDialog
          opened={bulkOpened}
          scope={t('app.buy.bulk.scopeLine', { goods: query.goods, basis: basisTitle, count: priceCount })}
          range={range}
          maxDiscount={maxDiscount}
          loading={bulk.isPending}
          onApply={(delta) => bulk.mutate(delta)}
          onClose={() => setBulkOpened(false)}
        />
      </Stack>
    </Container>
  );
}
